
import { Component, Vue, Watch } from 'vue-property-decorator';

import SelectField from '../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../framework/state';
import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../framework/client/resource';
import { getEnumerationOptions, getOptionLabel } from '../../../../framework/service/options';

import { Customer } from '../../../../../common/application/model/Customer';
import { getCustomerById, postCustomer } from '../../../client/netvisor';
import { CustomerType } from '../../../../../common/application/model/enums/CustomerType';
import { CustomerState } from '../../../../../common/application/model/enums/CustomerState';
import { getTagTypeFromCustomerState } from '../../../service/tag_type_service';
import { PaymentTerms } from '../../../../../common/application/model/enums/PaymentTerms';
import { getVehicleContractCounts } from '../../../client/vehicle_contract_client';
import { errorToast, successToast } from '../../../service/toast_service';
import { FrameworkUserRole } from '../../../../../common/framework/enumeration/FrameworkUserRole';
import { ApplicationUserRole } from '../../../../../common/application/enumeration/ApplicationUserRole';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import AutoCompleteMultipleField from '../../../fields/AutoCompleteMultipleField.vue';
import ParameterService from '../../../service/parameter_service';

@Component({
    components: {
        SelectField,
        AutoCompleteMultipleField,
    },
})
export default class Customers extends Vue {
    readonly resourceType = ApplicationResource.CUSTOMER;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as Customer[],
        total: 0,
        loading: false,
        selected: undefined as Customer | undefined,
        customerTypeOptions: [] as { id: string | undefined; label: string }[],
        customerStateOptions: [] as { id: string | undefined; label: string }[],
        paymentTermsOptions: [] as { id: string | undefined; label: string }[],
        contractCounts: [] as { id: string; name: string; vehiclecontracts: number }[],
        parameters: this.parameterService.get(),
    };

    async mounted() {
        this.local.customerTypeOptions = getEnumerationOptions('CustomerType', Object.keys(CustomerType), this);
        this.local.customerStateOptions = getEnumerationOptions('CustomerState', Object.keys(CustomerState), this);
        this.local.paymentTermsOptions = getEnumerationOptions('PaymentTerms', Object.keys(PaymentTerms), this);
        await this.loadAsyncData();
    }

    add() {
        this.$router.push(`/${ApplicationResource.CUSTOMER}/add`);
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    async sendToNetvisor(customerId: string) {
        try {
            this.local.loading = true;
            const result = await postCustomer(customerId);

            if (result.responseStatus.status !== 'OK') {
                errorToast(this, result.replies.insertedDataIdentifier);
            }

            await getCustomerById(customerId);
            await this.loadAsyncData();
            this.local.loading = false;
        } catch (error) {
            this.local.loading = false;
        }
    }

    async getNetvisorInfo(customerId: string) {
        try {
            this.local.loading = true;
            await getCustomerById(customerId);
            await this.loadAsyncData();
            this.local.loading = false;
        } catch (error) {
            this.local.loading = false;
        }
    }

    showCustomerFleet(customerId: string) {
        if (this.local.selected) {
            this.$router.push({
                path: `/${ApplicationResource.VEHICLE_CONTRACT}/${ApplicationResource.CUSTOMER}/${customerId}`,
            });
        }
    }

    importFleet(customerId: string): void {
        this.$router.push({ path: `/${ApplicationResource.CUSTOMER}/${customerId}/import-fleet` });
    }

    async onSortChange(field: string, order: 'desc' | 'asc') {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    @Watch('local.parameters.customerType')
    selectedCustomerTypeChange() {
        if (this.local.loading) {
            return;
        }

        this.resetPagingAndLoadData();
    }

    dropdownClosed(resourceIds: string[], idField: string): void {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.resetPagingAndLoadData();
    }

    hasContracts(customerId: string): boolean {
        let hasContracts = false;
        this.local.contractCounts.forEach((item) => {
            if (item.id === customerId && item.vehiclecontracts) {
                hasContracts = true;
            }
        });

        return hasContracts;
    }

    getCustomerTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.customerTypeOptions);
    }

    getCustomerStateLabel(id: string): string {
        return getOptionLabel(id, this.local.customerStateOptions);
    }

    getPaymentTermsLabel(id: string): string {
        return getOptionLabel(id, this.local.paymentTermsOptions);
    }

    getTagType(id: CustomerState): string {
        return getTagTypeFromCustomerState(id);
    }

    view() {
        if (this.local.selected) {
            this.$router.push(`/${ApplicationResource.CUSTOMER}/${this.local.selected.id}`);
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    get customerIds(): string[] {
        return this.local.parameters.id || [];
    }

    get isMobile(): boolean {
        return window.innerWidth < 769;
    }

    onRowClick(row: Customer): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    customerSelectorParameters(): Map<string, string> {
        const parameters = new Map<string, string>();

        if (this.local.parameters.customerType) {
            parameters.set('customerType', this.local.parameters.customerType);
        }

        return parameters;
    }

    getRowClass(row: Customer): string {
        if (row.freezeAssets) {
            return 'is-danger';
        }

        return '';
    }

    private async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();

        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;

        this.local.rows = [];

        const rows = await getResourcesNew<Customer>(this.resourceType, this.local.parameters.page, backendParameters);

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        this.local.contractCounts = await getVehicleContractCounts();

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.loading = false;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
